<template>
  <el-dialog
    class="notice-dialog"
    :show-close="false"
    v-model="visible"
    width="45%"
  >
    <!-- 头部 -->
    <div class="notice-dialog-header">
      <div class="header-left">
        <div class="logo">
          <img
            src="@/assets/logo.png"
            alt="logo"
          />
        </div>
        <div class="title">
          <div class="title-text">{{ curNoticeData.noticeTitle || '版本更新' }}</div>
          <div class="title-time">{{ curNoticeData.createTime || '' }}</div>
        </div>
      </div>
      <div class="header-right">最新版本： {{ latestVersionData.remark || '未知版本' }}</div>
    </div>
    <div
      v-loading="loading"
      class="notice-dialog-content"
    >
      <div class="notice-dialog-left">
        <!-- 历史版本 -->
        <div class="notice-list">
          <el-scrollbar
            height="400px"
            class="notice-dialog-body"
          >
            <div
              class="notice-item"
              v-for="(item, index) in noticeList"
              :key="index"
              :class="{ active: currentIndex === index }"
              @click="handleNoticeClick(index)"
            >
              <div class="notice-item-title-wrapper">
                <div class="notice-item-title">
                  <i class="ri-git-merge-line"></i>
                  <span class="notice-item-title-text">{{ item.remark || '未知版本' }}</span>
                </div>
                <!-- <span
                  v-if="index === 0"
                  class="new-tag"
                  >最新</span
                > -->
              </div>
              <div class="notice-item-time">{{ item.noticeTitle || '版本更新' }}</div>
            </div>
          </el-scrollbar>
        </div>
      </div>
      <div class="notice-dialog-right">
        <el-scrollbar
          height="400px"
          class="notice-dialog-body"
        >
          <div
            class="notice-content"
            v-html="curNoticeData.noticeContent || '暂无内容'"
          ></div>
        </el-scrollbar>
      </div>
      <div class="notice-dialog-close-btn">
        <el-button
          type="primary"
          @click="closeDialog"
        >
          我已知晓
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script setup>
import { ref } from 'vue';
import { getAppSysNoticePage } from '@/request';

const visible = ref(false); // 是否显示弹窗
const loading = ref(false); // 是否显示加载中
const noticeList = ref([]); // 通知公告数据
const curNoticeData = ref(null); // 当前通知公告数据
const currentIndex = ref(null); // 当前选中的通知索引
const latestVersionData = ref(null); // 最新版本数据

const emits = defineEmits(['handleClose']);

const showDialog = async (forceShow = false) => {
  const today = new Date().toISOString().slice(0, 10); // 获取当前日期，格式为"YYYY-MM-DD"
  const lastNoticeDate = localStorage.getItem('lastNoticeDate'); // 从本地存储获取上次显示通知的日期

  // 如果forceShow为真，或者今天还没有显示过通知
  if (forceShow || lastNoticeDate !== today) {
    await fetchAndDisplayNotice(today);
  } else {
    emits('handleClose');
  }
};

// 新封装的获取并显示通知的方法
const fetchAndDisplayNotice = async (today) => {
  loading.value = true;
  const hasData = await getNotice(); // 获取数据并检查是否有数据
  if (hasData) {
    visible.value = true; // 有数据时显示弹窗
    localStorage.setItem('lastNoticeDate', today); // 将今天的日期保存到本地存储
  } else {
    emits('handleClose');
  }
  loading.value = false; // 加载完成
};

// 获取通知公告
const getNotice = async () => {
  try {
    const res = await getAppSysNoticePage({ pageIndex: 1, pageSize: 10 });
    const { data, code } = res.data;
    if (code === 200 && data.list && data.list.length > 0) {
      noticeList.value = data.list;
      curNoticeData.value = data.list[0];
      currentIndex.value = 0;
      latestVersionData.value = data.list[0];
      return true;
    }

    return false;
  } catch (error) {
    console.error('Failed to fetch notice data:', error); // 打印错误信息
    return false; // 在发生错误时返回 false
  }
};

// 处理通知点击事件
const handleNoticeClick = (index) => {
  currentIndex.value = index;
  curNoticeData.value = noticeList.value[index];
};

// 关闭弹窗
const closeDialog = () => {
  visible.value = false;
  emits('handleClose');
};

defineExpose({ showDialog });
</script>

<style lang="scss">
.notice-dialog {
  position: relative;
  padding: 10px;
  border-radius: 12px !important;
  background: url('~@/assets/images/bg-img.jpg') center/200% no-repeat !important;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  backdrop-filter: blur(8px) !important;

  .el-dialog__header {
    display: none !important;
  }

  .notice-dialog-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    border-bottom: 1px solid $dark-color-3;

    .header-left {
      display: flex;
      align-items: center;
      .logo {
        box-sizing: border-box;
        width: 54px; // Logo宽度
        height: 54px; // Logo高度
        background: $dark-gradient-primary-2;
        border-radius: 12px;
        background-size: cover;
        overflow: hidden;
        background-position: center;
        display: flex;
        justify-content: center;
        align-items: center;
        img {
          width: 36px;
          height: 26px;
        }
      }
      .title {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: left;
        margin-left: 16px;
        .title-text {
          font-size: 26px;
          font-weight: 600;
        }
        .title-time {
          font-size: 12px;
          font-weight: 400;
          color: $dark-color-1;
          margin-top: 4px;
        }
      }
    }

    .header-right {
      padding: 0 10px;
      height: 32px;
      background: $dark-gradient-primary-2;
      border-radius: 12px;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 12px;
      font-weight: 400;
      color: $dark-color-0;
    }
  }

  .notice-dialog-content {
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    align-items: stretch;
    /* 确保子元素填满容器的高度 */

    .notice-dialog-left {
      padding: 10px;
      width: 25%;
      /* 左侧固定宽度为30% */
      // flex-shrink: 0; /* 防止左侧区域在空间不足时缩小 */
    }

    .notice-dialog-right {
      flex-grow: 1;
      /* 右侧区域自动填充剩余空间 */
      overflow: hidden;
      // min-width: 70%; /* 确保父容器宽度正确设置 */
      width: 70%;
      border-left: 1px solid #262627;
      padding: 10px;

      .notice-dialog-header {
        margin-bottom: 10px;
        // border-top-left-radius: 8px; // 设置左上角圆角
        border-radius: 8px; // 设置右上角圆角
        overflow: hidden;
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 50px;
        // background: linear-gradient(90deg, #5654f9 0%, #7f54f9 100%);
        background: #373737;
        // background: black;
        padding: 10px 20px; // 添加内边距以保持内容不紧贴边缘
      }

      .notice-title {
        display: flex;
        flex-direction: column;
        // align-items: center;
        // justify-content: center;
        color: white;
        font-size: 20px;
        font-weight: 600;

        .notice-title-text {
          font-size: 20px;
          font-weight: 600;
        }

        .notice-title-time {
          margin-top: 5px;
          font-size: 12px;
          font-weight: 400;
          color: #999;
        }
      }

      .system-logo {
        position: relative;
        width: 108px; // Logo宽度
        height: 98px; // Logo高度
        background-size: cover;
        overflow: hidden;
        background-position: center;
        transition: transform 0.3s ease-in-out; // 平滑变换效果

        &:hover {
          transform: scale(1.1); // 放大到110%原大小
        }

        .system-logo-img {
          position: absolute;
          bottom: -10px;
          left: 0;
          width: 100%;
          height: 100%;
          transition: transform 0.3s ease-in-out; // 平滑变换效果

          &:hover {
            transform: scale(1.1); // 放大到110%原大小
            transform-origin: right top; // 变换原点设置为右上角
          }
        }
      }
    }
  }

  .notice-dialog-body {
    height: 400px;
    overflow: hidden;
    // padding: 10px;
    display: flex;
    flex-direction: column;

    .notice-content {
      border-radius: 8px;
      padding: 10px;
      flex: 1;
      font-size: 14px;
      color: #666;
    }
  }

  .notice-list {
    /* 添加滚动条 */
    max-height: 400px;
    overflow-y: auto;
  }

  .notice-item {
    height: 64px;
    box-sizing: border-box;
    margin-top: 3px;
    // border-radius: 8px;
    padding: 10px;
    border-bottom: 1px solid #262627;
    /* 每个项目之间的分隔线 */
    cursor: pointer;
    transition: background-color 0.3s;
  }

  .notice-item:hover {
    // transition: all 0.3s;
    position: relative;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    background: #373737;
  }

  .notice-item.active {
    border-radius: 8px;
    background: $dark-gradient-primary-2;
  }

  .notice-item-title-wrapper {
    display: flex;
    align-items: center;
  }

  .notice-item-title {
    display: flex;
    align-items: center;
    font-size: 14px;
    font-weight: bold;
    color: #fff;
    width: 140px;
    /* 设置最大宽度为100px */
    overflow: hidden;
    /* 隐藏超出部分 */
    white-space: nowrap;
    /* 防止文本换行 */
    text-overflow: ellipsis;
    /* 超出部分显示省略号 */

    i {
      font-size: 20px;
      margin-right: 4px;
      font-weight: 300 !important;
    }
  }

  .notice-item-time {
    font-size: 12px;
    color: #999;
    margin-top: 5px;
    width: 100%;
    /* 设置最大宽度为100px */
    overflow: hidden;
    /* 隐藏超出部分 */
    white-space: nowrap;
    /* 防止文本换行 */
    text-overflow: ellipsis;
    /* 超出部分显示省略号 */
  }

  .new-tag {
    background-color: #ff5722;
    /* 最新标签的背景色 */
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    margin-left: 10px;
  }
}

.notice-dialog-close-btn {
  position: absolute;
  left: 50%; // 将按钮移动到容器的中心
  bottom: -40px; // 保持在底部，但稍微向上10px
  transform: translateX(-50%); // 使用变换将按钮向左移动自身宽度的50%，以确保真正居中
  font-size: 30px;
  cursor: pointer;

  :deep(.el-button) {
    background: $dark-gradient-primary !important;
    color: $dark-color-0 !important;
    border: none !important;
  }
}
</style>
